/**
 * Chat attachments — constants, helpers and the chip/lightbox components.
 * Extracted verbatim from AgentPage.jsx (Phase 2 chat-core split) so the
 * standalone chat page and any embedded chat surface share ONE implementation.
 * Behavior-frozen: logic and markup are unchanged from the page version.
 *
 * Document files (PDF/DOCX/PPTX/XLSX/CSV/TXT) are extracted to text in-memory
 * (the supporting-doc pipeline) — a spreadsheet arrives as its rows, sheet by
 * sheet. Images are downscaled and sent as base64 so a vision-capable model can
 * actually SEE them; the raw original file is never stored on the server.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { FiFileText, FiX, FiTrash2, FiChevronRight, FiChevronLeft, FiImage, FiFile, FiGrid, FiAlertCircle, FiDownload } from 'react-icons/fi';
import { aiAPI } from '../services/api';

export const MAX_ATTACHMENTS = 4;
export const ATTACH_ACCEPT = '.pdf,.jpg,.jpeg,.png,.txt,.csv,.docx,.pptx,.xlsx';
export const ATTACH_EXT = /\.(pdf|jpe?g|png|txt|csv|docx|pptx|xlsx)$/i;
// The human list behind ATTACH_EXT, for the "unsupported file" toast. Kept
// beside the regex so a format added to one is added to the other.
export const ATTACH_TYPES_LABEL = 'PDF, image, Excel, CSV, TXT, DOCX or PPTX';
export const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
// Mirrors MAX_FILE_SIZE in backend/services/supportingDocumentExtractor.js
// (and ChatAttachment.MAX_FILE_BYTES, the storage cap for the same file).
// Lives here so both composers reject oversized files before the upload.
export const MAX_ATTACH_BYTES = 15 * 1024 * 1024;
// The number every "too large" line quotes — derived, so raising the cap above
// never leaves the UI telling users an out-of-date limit.
export const MAX_ATTACH_MB = Math.round(MAX_ATTACH_BYTES / (1024 * 1024));

// Match the backend caps so the UI and server agree on what's sent.
// Matches backend MAX_ATTACHMENT_CHARS (chat/contracts.js). ~24k chars ≈ 6k
// tokens per document; with the 4-attachment cap that's ≈24k tokens worst case —
// deliberately below the 60k-char server ceiling in supportingDocumentExtractor
// so an admin can raise the per-request cap without this constant fighting it.
export const CHAT_ATTACH_MAX_CHARS = 24000; // backend MAX_ATTACHMENT_CHARS
export const IMAGE_MAX_DIM = 1568; // longest-edge cap (Anthropic's recommended max) — keeps payload + tokens sane
// Re-send full image bytes only for the most recent N attachment turns (matches
// the backend's ATTACHMENT_MEMORY_TURNS) so long chats don't balloon the payload.
export const ATTACH_MEMORY_TURNS = 2;

// Read an image File, downscale its longest edge to IMAGE_MAX_DIM, and return
// { mimeType, data, dataUrl } where `data` is bare base64. JPEG keeps payloads
// small; PNG is preserved for images that may rely on transparency.
export const imageToBase64 = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    try {
      const scale = Math.min(1, IMAGE_MAX_DIM / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      const isPng = /png$/i.test(file.type) || /\.png$/i.test(file.name || '');
      const mimeType = isPng ? 'image/png' : 'image/jpeg';
      const dataUrl = canvas.toDataURL(mimeType, 0.85);
      URL.revokeObjectURL(url);
      const comma = dataUrl.indexOf(',');
      resolve({ mimeType, data: comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl, dataUrl });
    } catch (e) { URL.revokeObjectURL(url); reject(e); }
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not load image')); };
  img.src = url;
});

export const attachIcon = (fileType) => (
  fileType === 'image' ? FiImage
    : fileType === 'pdf' ? FiFileText
      : fileType === 'sheet' ? FiGrid
        : FiFile
);

// Classify a File locally (before extraction) so the chip shows the right icon /
// thumbnail immediately and we know whether to build an image preview.
export const localFileType = (file) => {
  const t = String(file.type || '').toLowerCase();
  const n = String(file.name || '').toLowerCase();
  if (t.startsWith('image/') || /\.(jpe?g|png)$/.test(n)) return 'image';
  if (t === 'application/pdf' || n.endsWith('.pdf')) return 'pdf';
  if (n.endsWith('.docx')) return 'docx';
  if (n.endsWith('.pptx')) return 'pptx';
  // Spreadsheets share one chip type: to the reader an .xlsx and a .csv are the
  // same thing, and the backend picks its own extractor from the extension.
  if (n.endsWith('.xlsx') || n.endsWith('.csv')) return 'sheet';
  if (t === 'text/plain' || n.endsWith('.txt')) return 'text';
  return 'file';
};

export const TYPE_LABEL = { image: 'Image', pdf: 'PDF', docx: 'Word', pptx: 'Slides', sheet: 'Spreadsheet', text: 'Text', file: 'File' };
export const formatBytes = (n) => {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// Provider/extraction errors are raw and long ("You exceeded your current quota…").
// Map the common ones to a short, actionable line for the chip + preview.
export const friendlyAttachError = (msg) => {
  const m = String(msg || '').toLowerCase();
  // Size/type checks first: a 413 body says "entity too large" and would
  // otherwise be swallowed by the quota branch ("exceed").
  if (/413|entity too large|payload too large|too large to upload/.test(m)) return `File is too large to upload (max ${MAX_ATTACH_MB} MB).`;
  if (/too large/.test(m)) return `File is too large — max ${MAX_ATTACH_MB} MB.`;
  if (/does not match declared type/.test(m)) return 'File content doesn’t match its extension.';
  if (/quota|exceed|rate limit|429|busy/.test(m)) return 'Image reading is busy — try again, or attach a PDF/text instead.';
  if (/unsupported|cannot|not support/.test(m)) return 'This file type couldn’t be read.';
  if (/no readable|empty|no text/.test(m)) return 'No readable text found in this file.';
  return 'Could not read this file.';
};

// A Claude-style attachment card. Shows an image thumbnail (or a type icon tile),
// the filename, and a type · size sub-line. In the composer it's clickable to
// preview (onOpen) and removable (corner ✕). Static on a sent message (neither).
export function AttachmentChip({ att, onRemove, onOpen }) {
  const Ico = attachIcon(att.fileType);
  const error = att.status === 'error';
  const extracting = att.status === 'extracting';
  const isImage = att.fileType === 'image' && att.previewUrl;
  const clickable = !!onOpen;
  const sub = error
    ? friendlyAttachError(att.error)
    : `${TYPE_LABEL[att.fileType] || 'File'}${att.size ? ` · ${formatBytes(att.size)}` : extracting ? ' · reading…' : ''}`;

  // self-end keeps the card at its natural height: as a flex child next to a
  // taller sibling (an inline image on a sent turn) it would otherwise stretch
  // to match and render as a giant empty card. `end` over `start` so it lines
  // up on the bottom edge of whatever it sits beside.
  return (
    <div
      className="group/att relative self-end inline-flex items-center max-w-[240px] rounded-xl border bg-white transition-shadow hover:shadow-[0_6px_18px_-12px_rgba(8,17,31,0.35)]"
      style={{ borderColor: error ? '#e6c3bc' : '#e2d9c8', background: error ? '#fbf2f0' : '#fff' }}
    >
      <button
        type="button"
        onClick={clickable ? () => onOpen(att) : undefined}
        disabled={!clickable}
        title={clickable ? `Preview ${att.name}` : att.name}
        className={`flex items-center gap-2.5 min-w-0 text-left pl-1.5 pr-3 py-1.5 rounded-xl ${clickable ? 'cursor-pointer' : 'cursor-default'}`}
      >
        <div
          className="relative w-9 h-9 rounded-lg overflow-hidden shrink-0 flex items-center justify-center"
          style={{ background: isImage ? '#f0ece2' : error ? 'rgba(154,86,74,0.1)' : 'rgba(20,95,95,0.08)' }}
        >
          {isImage
            ? <img src={att.previewUrl} alt="" className="w-full h-full object-cover" />
            : <Ico size={16} style={{ color: error ? '#9a564a' : '#145f5f' }} />}
          {extracting && (
            <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.6)' }}>
              <span className="w-3.5 h-3.5 rounded-full border-2 border-[#ddd6c6] border-t-[#145f5f] animate-spin" />
            </div>
          )}
        </div>
        <div className="min-w-0">
          <div className="text-[12px] font-semibold text-[#08111f] truncate leading-tight">{att.name}</div>
          <div className="text-[10px] tracking-[0.3px] truncate mt-0.5" style={{ color: error ? '#9a564a' : '#9a9080' }}>{sub}</div>
        </div>
      </button>
      {onRemove && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(att.id); }}
          className="absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center text-white shadow-sm transition-transform active:scale-90 hover:scale-110"
          style={{ background: '#08111f' }}
          aria-label={`Remove ${att.name}`}
        >
          <FiX size={12} />
        </button>
      )}
    </div>
  );
}

// Minimal lightbox-style attachment preview with navigation. The current item
// fills the screen on a dark mat — images zoom on click (1×→2×→3×→reset) with
// drag-to-pan; other files show their extracted text in a card. With several
// attachments, prev/next arrows, ←/→ keys, a counter and a thumbnail strip switch
// between them.
const ZOOM_STEPS = [1, 2, 3];

export function AttachmentPreview({ items, currentId, onSelect, onClose, onRemove }) {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const drag = useRef(null); // { startX, startY, baseX, baseY } while panning
  const moved = useRef(false); // did the pointer actually move during a drag?
  const [dragging, setDragging] = useState(false);

  const list = Array.isArray(items) ? items : [];
  const idx = list.findIndex((a) => a.id === currentId);
  const att = idx >= 0 ? list[idx] : null;
  const count = list.length;

  // Restored documents (remote:true, no local text) live server-side in
  // ChatAttachment — fetch on open. { status: 'loading'|'ready'|'error',
  // url?, text? }. Signed URLs expire in ~5 min so nothing is cached across
  // opens; same-origin /raw needs the auth header, so it goes through the
  // API client as a blob → object URL.
  const [remote, setRemote] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let objUrl = null;
    setRemote(null);
    if (!att || att.fileType === 'image' || att.text || !att.remote
      || att.status === 'error' || att.status === 'extracting') return undefined;
    (async () => {
      setRemote({ status: 'loading' });
      try {
        const res = await aiAPI.getChatAttachmentUrl(att.id);
        const data = res?.data || res || {};
        let url = data.url;
        if (!url) throw new Error('no url');
        // Plain-text formats render as text in the card; anything else needs a
        // URL the browser can open. CSV counts as text — a restored one shown
        // as rows beats a blob the viewer can only download.
        const isTxt = /\.(txt|csv)$/i.test(att.name || data.name || '');
        if (String(url).startsWith('/api/')) {
          const blob = await aiAPI.getChatAttachmentBlob(att.id);
          if (isTxt) {
            const text = await blob.text();
            if (!cancelled) setRemote({ status: 'ready', text });
            return;
          }
          objUrl = URL.createObjectURL(blob);
          url = objUrl;
        } else if (isTxt) {
          const text = await (await fetch(url)).text();
          if (!cancelled) setRemote({ status: 'ready', text });
          return;
        }
        if (!cancelled) setRemote({ status: 'ready', url });
      } catch {
        // 404 = genuinely not persisted (consent-off chat, pre-feature
        // history, aged out) — the "contents aren't kept" card is the truth.
        if (!cancelled) setRemote({ status: 'error' });
      }
    })();
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [att]);

  const go = useCallback((d) => {
    if (count < 2) return;
    const n = (idx + d + count) % count;
    onSelect(list[n].id);
  }, [count, idx, list, onSelect]);

  // Reset zoom/pan whenever the shown item changes.
  useEffect(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, [currentId]);

  // Keyboard: Esc closes; ←/→ navigate between attachments.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, go]);

  // Pan drag (image only, when zoomed).
  useEffect(() => {
    if (!dragging) return undefined;
    const move = (e) => {
      if (!drag.current) return;
      if (Math.abs(e.clientX - drag.current.startX) + Math.abs(e.clientY - drag.current.startY) > 3) moved.current = true;
      setPan({ x: drag.current.baseX + (e.clientX - drag.current.startX), y: drag.current.baseY + (e.clientY - drag.current.startY) });
    };
    const up = () => { drag.current = null; setDragging(false); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, [dragging]);

  if (!att) return null;
  const isImage = att.fileType === 'image' && att.previewUrl;
  const error = att.status === 'error';
  const extracting = att.status === 'extracting';
  const Ico = attachIcon(att.fileType);
  const remoteLoading = remote?.status === 'loading';
  const remoteReady = remote?.status === 'ready';
  // A fetched PDF renders inline, filling the stage like an image does.
  const isPdfRemote = remoteReady && remote.url && /\.pdf$/i.test(att.name || '');
  const shownText = att.text || (remoteReady ? remote.text : '');

  const cycleZoom = () => {
    const next = ZOOM_STEPS[(ZOOM_STEPS.indexOf(zoom) + 1) % ZOOM_STEPS.length];
    setZoom(next);
    if (next === 1) setPan({ x: 0, y: 0 });
  };
  const onImgMouseDown = (e) => {
    if (zoom <= 1) return;
    e.preventDefault();
    moved.current = false;
    drag.current = { startX: e.clientX, startY: e.clientY, baseX: pan.x, baseY: pan.y };
    setDragging(true);
  };

  // Remove the current attachment; jump to a neighbour if any remain, else close.
  const handleRemove = () => {
    if (count <= 1) { onRemove(att.id); onClose(); return; }
    const nextId = list[(idx + 1) % count].id; // valid neighbour (count > 1)
    onRemove(att.id);
    onSelect(nextId);
  };

  const arrowBtn = 'absolute z-10 w-10 h-10 rounded-full flex items-center justify-center text-white/85 bg-white/10 hover:bg-white/20 transition-colors';

  return (
    <div className="fixed inset-0 z-[80] flex flex-col animate-in fade-in duration-150" style={{ background: 'rgba(8,17,31,0.9)' }} onClick={onClose}>
      {/* Top bar — name · size · counter · Remove · Close */}
      <div className="flex items-center gap-2 px-4 py-3 shrink-0" onClick={(e) => e.stopPropagation()}>
        <span className="text-[12px] text-white/85 truncate">{att.name}</span>
        {att.size ? <span className="text-[11px] text-white/40 shrink-0">{formatBytes(att.size)}</span> : null}
        {count > 1 && <span className="text-[11px] text-white/50 shrink-0">· {idx + 1} / {count}</span>}
        <div className="ml-auto flex items-center gap-1.5 shrink-0">
          {remoteReady && remote.url && (
            <a
              href={remote.url}
              download={att.name || 'document'}
              className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-full text-[11.5px] font-semibold text-white/90 hover:bg-white/15 transition-colors"
              aria-label={`Download ${att.name}`}
            >
              <FiDownload size={13} /> Download
            </a>
          )}
          {onRemove && (
            <button onClick={handleRemove} className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-full text-[11.5px] font-semibold text-white/90 hover:bg-white/15 transition-colors" aria-label="Remove attachment">
              <FiTrash2 size={13} /> Remove
            </button>
          )}
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center text-white/80 hover:bg-white/15 transition-colors" aria-label="Close preview">
            <FiX size={18} />
          </button>
        </div>
      </div>

      {/* Stage */}
      <div className="relative flex-1 min-h-0 flex items-center justify-center px-4 sm:px-16" onClick={onClose}>
        {count > 1 && (
          <button onClick={(e) => { e.stopPropagation(); go(-1); }} className={`${arrowBtn} left-2 sm:left-4`} aria-label="Previous attachment">
            <FiChevronLeft size={22} />
          </button>
        )}

        {isImage ? (
          <img
            src={att.previewUrl}
            alt={att.name}
            draggable={false}
            onClick={(e) => { e.stopPropagation(); if (moved.current) { moved.current = false; return; } cycleZoom(); }}
            onMouseDown={onImgMouseDown}
            className="select-none"
            style={{
              maxWidth: '88vw',
              maxHeight: '100%',
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transition: dragging ? 'none' : 'transform 0.2s ease',
              cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'zoom-in',
            }}
          />
        ) : isPdfRemote ? (
          // Fetched PDF — inline viewer filling the stage, image-style.
          <object
            type="application/pdf"
            data={remote.url}
            aria-label={att.name}
            className="w-full h-full max-w-[920px] rounded-xl bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-center h-full text-[13px] text-white/70">
              This browser can’t display PDFs inline — use Download above.
            </div>
          </object>
        ) : (
          <div className="w-full max-w-[640px] max-h-full flex flex-col rounded-2xl bg-white overflow-hidden" style={{ boxShadow: '0 24px 64px -16px rgba(8,17,31,0.5)' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex-1 overflow-auto cs-content-scroll">
              {extracting || remoteLoading ? (
                <div className="flex items-center justify-center gap-2 text-[13px] text-[#7a7060] py-16">
                  <span className="w-4 h-4 rounded-full border-2 border-[#ddd6c6] border-t-[#145f5f] animate-spin" /> {remoteLoading ? 'Fetching file…' : 'Reading file…'}
                </div>
              ) : error ? (
                <div className="flex flex-col items-center text-center gap-2 px-8 py-12">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(154,86,74,0.1)', color: '#9a564a' }}><FiAlertCircle size={22} /></div>
                  <div className="text-[13.5px] font-semibold text-[#08111f]">Couldn’t read this file</div>
                  <div className="text-[12.5px] text-[#7a7060] max-w-[420px]">{friendlyAttachError(att.error)}</div>
                </div>
              ) : shownText ? (
                <pre className="whitespace-pre-wrap break-words text-[12.5px] leading-relaxed text-[#1a2230] px-6 py-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {shownText}
                </pre>
              ) : remoteReady && remote.url ? (
                // Fetched but not inline-renderable (DOCX) — offer the file.
                <div className="flex flex-col items-center text-center gap-2 px-8 py-12">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(20,95,95,0.08)', color: '#145f5f' }}>
                    <Ico size={22} />
                  </div>
                  <div className="text-[13.5px] font-semibold text-[#08111f]">{att.name}</div>
                  <div className="text-[12.5px] text-[#7a7060] max-w-[420px]">
                    Word documents can’t be shown inline — download to open it.
                  </div>
                  <a
                    href={remote.url}
                    download={att.name || 'document'}
                    className="inline-flex items-center gap-1.5 mt-1 px-3.5 py-2 rounded-xl text-[12px] font-semibold text-white transition-transform active:scale-95"
                    style={{ background: '#145f5f' }}
                  >
                    <FiDownload size={13} /> Download
                  </a>
                </div>
              ) : (
                // Nothing local and nothing on the server: a consent-off /
                // private chat, pre-feature history, or the file aged out.
                // Say that plainly instead of showing a blank page.
                <div className="flex flex-col items-center text-center gap-2 px-8 py-12">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(20,95,95,0.08)', color: '#145f5f' }}>
                    <Ico size={22} />
                  </div>
                  <div className="text-[13.5px] font-semibold text-[#08111f]">{att.name}</div>
                  <div className="text-[12.5px] text-[#7a7060] max-w-[420px]">
                    This file’s contents aren’t available anymore — only its name and type were kept. Attach it again to read it here.
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {count > 1 && (
          <button onClick={(e) => { e.stopPropagation(); go(1); }} className={`${arrowBtn} right-2 sm:right-4`} aria-label="Next attachment">
            <FiChevronRight size={22} />
          </button>
        )}
      </div>

      {/* Footer — zoom hint + thumbnail strip */}
      <div className="shrink-0 pb-3 pt-1" onClick={(e) => e.stopPropagation()}>
        {isImage && (
          <div className="text-center text-[11px] text-white/45 mb-2 pointer-events-none">
            {zoom > 1 ? `${zoom}× · drag to pan · click to ${zoom === ZOOM_STEPS[ZOOM_STEPS.length - 1] ? 'reset' : 'zoom'}` : 'Click image to zoom'}
          </div>
        )}
        {count > 1 && (
          <div className="flex items-center justify-center gap-2 px-4 overflow-x-auto cs-content-scroll">
            {list.map((it, i) => {
              const ItIco = attachIcon(it.fileType);
              const active = i === idx;
              const thumbImg = it.fileType === 'image' && it.previewUrl;
              return (
                <button
                  key={it.id}
                  onClick={() => onSelect(it.id)}
                  title={it.name}
                  className="relative w-11 h-11 rounded-lg overflow-hidden shrink-0 flex items-center justify-center transition-all"
                  style={{
                    background: thumbImg ? '#000' : 'rgba(255,255,255,0.1)',
                    outline: active ? '2px solid #fff' : '2px solid transparent',
                    opacity: active ? 1 : 0.55,
                  }}
                  aria-label={`View ${it.name}`}
                  aria-current={active}
                >
                  {thumbImg
                    ? <img src={it.previewUrl} alt="" className="w-full h-full object-cover" />
                    : <ItIco size={16} className="text-white/80" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
